import { Component, OnInit } from '@angular/core';
import { ApiRoutes } from '@sb/library/api';
import { BaseViewComponent, CreditCardType, ErrorMessages, FormBase, NameCodeWithID, Optional, PaymentContext, PaymentContextScope, PaymentProcessingScope, PaymentSourceMode, Required, Validators, boolean, string } from '@sb/library/base';
import { CreditCardBrand, NameCodePair, PaymentProcessorType, PaymentSourceType, SurchargeType } from '@sb/library/data-models';
import { SBDate } from '@sb/library/localization';

class PayPalAddPaymentSourceForm extends FormBase {
    HolderName = Required(string);
    CardType = Required(CreditCardType);
    AccountNumber = Required(string);
    ExpiryDate = Required(SBDate);
    CVV = Required(string);
    Country = Required(NameCodeWithID);
    State = Required(NameCodeWithID);
    City = Required(string);
    ZipCode = Required(string);
    Address1 = Required(string);
    Address2 = Optional(string);
    AutoPay = Optional(boolean);
    SavePaymentSource = Optional(boolean);
}

@Component({
    selector: 'lib-payment-sources-add-paypal-pro',
    templateUrl: './payment-sources-add-paypal-pro.component.html',
    styleUrls: ['./payment-sources-add-paypal-pro.component.scss']
})
export class PaymentSourcesAddPaypalProComponent extends BaseViewComponent implements OnInit {

    Scope = this.SetScope({
        Requires: [PaymentContextScope, PaymentProcessingScope]
    });

    readonly addPayPalPaymentMethodForm = PayPalAddPaymentSourceForm.Create(false);

    readonly cardTypeError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceCreditCardTypeMissingError:Please select your card type`
    };

    readonly cardHolderError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceCreditCardHolderMissingError:Please enter the name of the card holder`
    };

    readonly cardNumberError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceCreditCardNumberMissingError:Please enter your card number`,
        exactlength: (value: {requiredLength: number, actualLength: number}) =>
            $localize`:@@CommonAddPaymentSourceCardNumberWrongLengthError:${this.addPayPalPaymentMethodForm.controls.CardType.value?.Name} number must be ${value.requiredLength} digits long`
    };

    readonly cardExpiryDateError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceCreditCardExpiryDateMissingError:Please enter the card's expiry date`
    };

    readonly cardCVVError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceCreditCardCVVMissingError:Please enter the CVV (Card Verification Value)`,
        exactlength: (value: {requiredLength: number, actualLength: number}) =>
            $localize`:@@CommonAddPaymentSourceCVVWrongLengthError:${this.addPayPalPaymentMethodForm.controls.CardType.value?.Name} CVV must be ${value.requiredLength} digits long`
    };

    readonly countryError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceCountryMissingError:Please select your country`
    };

    readonly stateError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceStateProvinceMissingError:Please select your state or province`
    };

    readonly cityError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceCityMissingError:Please enter your city`
    };

    readonly zipCodeError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceZipCodeMissingError:Please enter your zip/postal code`
    };

    readonly addressError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceAddressMissingError:Please enter your address`
    };

    readonly bankNameError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceBankNameMissingError:Please enter the name of your bank`
    }

    readonly accountHolderError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceBankAccountHolderMissingError:Please enter the name of the account holder`
    };

    readonly accountTypeError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceBankAccountTypeMissingError:Please select your account type`
    };

    readonly accountNumberError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceBankAccountNumberMissingError:Please enter your bank account number`,
        minlength: (value: {requiredLength: number, actualLength: number}) =>
            $localize`:@@CommonAddPaymentSourceBankAccountNumberTooShortError:Account number must be at least ${value.requiredLength} digits long`,
        maxlength: (value: {requiredLength: number, actualLength: number}) =>
            $localize`:@@CommonAddPaymentSourceBankAccountNumberTooLongError:Account number must be at most ${value.requiredLength} digits long`
    };

    readonly routingNumberError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceBankRoutingNumberMissingError:Please enter the routing number for your bank account`,
        exactlength: (value: {requiredLength: number, actualLength: number}) =>
            $localize`:@@CommonAddPaymentSourceBankRoutingNumberWrongLengthError:Routing number must be ${value.requiredLength} digits long`
    };

    readonly consentError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceBankConsentRequiredError:You must provide consent to debit your bank account`
    }

    readonly autoPayError: ErrorMessages = {
        required: _ => $localize`:@@CommonAddPaymentSourceAutoPayRequiredError:Your #Studio# requires that Auto Pay be enabled`
    }

    readonly minDate = SBDate.StartOfMonth();
    readonly maxDate: SBDate = this.minDate.Clone().AddYears(15);

    private _creditCardTypes: CreditCardType[] = [];
    private _countries: NameCodeWithID[] = [];
    private _states: NameCodeWithID[] = [];
    private _cvvPlaceholder: string = '';
    private _cvvTooltip: string = $localize`:@@CommonAddPaymentSourceCardGenericCVVTooltip:Your CVV (Card Verification Value) is displayed on the back of your card as the last 3 digits`;
    private _paymentProcessorError: string | null = null;
    private _defaultState: NameCodeWithID = {
        ID: 'default',
        Name: $localize`:@@CommonAddPaymentSourceCountryStatesDefault:Outside US/Canada`,
        Code: null,
        Default: true
    };

    override ngOnInit(): void {
        super.ngOnInit();

        if (this.Scope.paymentProcessor!.Type != PaymentProcessorType.PayPalPro) {
            throw Error('Wrong payment processor type for payment-sources-add-paypal-pro.component.ts')
        }

        this._creditCardTypes = this.Scope.paymentProcessor!.CreditCardsMetaData.map(card => new CreditCardType({...card, ID: card.Brand}));

        this.GetCountries();
        this.addPayPalPaymentMethodForm.controls.SavePaymentSource.setValue(this.Scope.mode !== PaymentSourceMode.AnonymousPayment);
        this.addPayPalPaymentMethodForm.Disable();

        this.Scope.PaymentSourceTypeChanged?.(PaymentSourceType.CreditCard);
    }

    GetStates(country: NameCodePair) {
        ApiRoutes.Country.GetProvStates.Call({Parameter: country.Code!}).subscribe(states => {
            if (states && states.length > 0) {
                this._states = states.map(state => new NameCodeWithID({...state, ID: state.Code!}));

                const defaultState = this._states.find(state => state.Default);
                if (defaultState) {
                    this.addPayPalPaymentMethodForm.controls.State.setValue(defaultState);
                } else {
                    this.addPayPalPaymentMethodForm.controls.State.setValue(null);
                }
            } else {
                this._states = [this._defaultState];
                this.addPayPalPaymentMethodForm.controls.State.setValue(this._defaultState);
            }
        });
    }

    CardTypeChanged(card: CreditCardType) {
        this.addPayPalPaymentMethodForm.controls.AccountNumber.AddValidators([Validators.required, Validators.exactLength(card.NumberLength, '[0-9]')]);
        this.addPayPalPaymentMethodForm.controls.AccountNumber.updateValueAndValidity();

        this.addPayPalPaymentMethodForm.controls.CVV.AddValidators([Validators.required, Validators.exactLength(card.CvcLength)]);
        this.addPayPalPaymentMethodForm.controls.CVV.updateValueAndValidity();

        if (card.Brand === CreditCardBrand.Amex) {
            this._cvvTooltip = $localize`:@@CommonAddPaymentSourceCardAmexCVVTooltip:Your CVV (Card Verification Value) is the ${card.CvcLength} digits above the last few digits on the front of the card`;
        } else {
            this._cvvTooltip = $localize`:@@CommonAddPaymentSourceCardKnownLengthCVVTooltip:Your CVV (Card Verification Value) is displayed on the back of your card as the last ${card.CvcLength} digits`;
        }

        this._cvvPlaceholder = '1234567890'.slice(0, card.CvcLength);
    }

    SavePaymentMethodForFuture(savePaymentMethod: boolean): void {
        if (!savePaymentMethod) {
            this.addPayPalPaymentMethodForm.controls.AutoPay.setValue(false);
        }
    }

    private GetCountries() {
        ApiRoutes.Country.Get.Call().subscribe(countries => {
            this._countries = countries.map(country => new NameCodeWithID({...country, ID: country.Code!}));

            let defaultCountry: NameCodeWithID | undefined;

            defaultCountry = this._countries.find(country => country.Default);

            if (!defaultCountry) {
                defaultCountry = this._countries[0];
            }

            this.addPayPalPaymentMethodForm.controls.Country.setValue(defaultCountry);
            this.GetStates(defaultCountry);
        });
    }
    get creditCardTypes(): CreditCardType[] {
        return this._creditCardTypes;
    }

    get countries(): NameCodeWithID[] {
        return this._countries;
    }

    get states(): NameCodeWithID[] {
        return this._states;
    }

    get cvvPlaceholder(): string {
        return this._cvvPlaceholder;
    }

    get cvvTooltip(): string {
        return this._cvvTooltip;
    }

    get cardSurcharge() {
        return this.Scope.schoolInfo.PaymentSurchargeCreditCard;
    }

    get paymentProcessorError(): string | null {
        return this._paymentProcessorError;
    }

    get PaymentSourceType(): typeof PaymentSourceType {
        return PaymentSourceType;
    }

    get SurchargeType(): typeof SurchargeType {
        return SurchargeType;
    }

    get PaymentSourceMode(): typeof PaymentSourceMode {
        return PaymentSourceMode;
    }

    get PaymentContext(): typeof PaymentContext {
        return PaymentContext;
    }

    get forceAutoPay(): boolean {
        return true;
    }
}
